import { DatePickerWithRange } from '@/entities/datePicker'
import { BaseDialog, BaseMultiSelect, BaseCheckbox } from '@/shared/components'
import { BaseButtonGroup } from '@/shared/components/BaseButtonGroup'
import { useActiveUser } from '@/shared/hooks/useActiveUser'
import { useToast } from '@/shared/hooks'
import { downloadFile } from '@/shared/lib/downloadFile'

import { useState, useMemo } from 'react'
import { DateRange } from 'react-day-picker'
import { EXPORT_TYPES, STATUS_OPTIONS_BY_TYPE, EXPORT_FORMATS, type ExportType } from '../model'
import { generateExportFilename } from '../lib'
import { useExport } from '../api'
import { normalizeCsvByExportType } from '@/shared/lib/normalizeCsv'

type Props = {
  modalExport: boolean
  setModalExport: (value: boolean) => void
}

// Тип для отслеживания выбранных статусов для каждого типа экспорта
type SelectedStatusesByType = Record<ExportType, number[]>

const ALL_STATUSES_VALUE = -1
const ALL_STATUSES_OPTION = { value: ALL_STATUSES_VALUE.toString(), label: 'Все статусы' }

export const DialogExport = ({ modalExport, setModalExport }: Props) => {
  const { userRole } = useActiveUser()
  const { addToast } = useToast()
  const { exportData, isLoading: isExporting } = useExport()

  const [dateRange, setDateRange] = useState<DateRange | undefined>()
  const [selectedExportTypes, setSelectedExportTypes] = useState<Set<ExportType>>(new Set())
  const [selectedStatusesByType, setSelectedStatusesByType] = useState<SelectedStatusesByType>({
    balances: [],
    withdrawals: [],
    topups: [],
    cryptowithdrawals: [],
  })

  // Получаем доступные типы экспорта для текущего пользователя
  const availableExportTypes = useMemo(() => (userRole ? EXPORT_TYPES[userRole] : []), [userRole])

  const handleExportTypeChange = (exportType: ExportType) => {
    const newSelected = new Set(selectedExportTypes)
    if (newSelected.has(exportType)) {
      newSelected.delete(exportType)
    } else {
      newSelected.add(exportType)

      // Если у типа есть статусы, выбираем "Все статусы" по умолчанию
      const hasStatuses = STATUS_OPTIONS_BY_TYPE[exportType].length > 0
      if (hasStatuses) {
        setSelectedStatusesByType((prev) => ({
          ...prev,
          [exportType]: [ALL_STATUSES_VALUE],
        }))
      }
    }
    setSelectedExportTypes(newSelected)
  }

  const handleStatusChange = (exportType: ExportType, newStatuses: number[]) => {
    const prevStatuses = selectedStatusesByType[exportType] || []

    const isAllSelectedNow = newStatuses.includes(ALL_STATUSES_VALUE)
    const wasAllSelected = prevStatuses.includes(ALL_STATUSES_VALUE)

    // Получаем все возможные статусы для данного типа
    const allPossibleStatuses = STATUS_OPTIONS_BY_TYPE[exportType].map((s) => s.value)
    const realStatusesSelected = newStatuses.filter((s) => s !== ALL_STATUSES_VALUE)
    // Если выбраны все реальные статусы вручную
    const areAllRealStatusesSelected =
      realStatusesSelected.length > 0 && realStatusesSelected.length === allPossibleStatuses.length

    let finalStatuses: number[]

    if (isAllSelectedNow && !wasAllSelected) {
      // Только что кликнули "Все статусы" -> сбрасываем остальные, оставляем только "Все"
      finalStatuses = [ALL_STATUSES_VALUE]
    } else if (wasAllSelected && isAllSelectedNow && newStatuses.length > 1) {
      // Было выбрано "Все", пользователь кликнул еще какой-то статус -> снимаем "Все", оставляем новый выбор
      finalStatuses = newStatuses.filter((s) => s !== ALL_STATUSES_VALUE)
    } else if (areAllRealStatusesSelected) {
      // Пользователь вручную выбрал все доступные статусы -> переключаем на "Все статусы"
      finalStatuses = [ALL_STATUSES_VALUE]
    } else if (!isAllSelectedNow && wasAllSelected) {
      // Пользователь снял галочку "Все статусы" -> значит ничего не выбрано
      finalStatuses = []
    } else {
      // Обычный выбор
      finalStatuses = newStatuses
    }

    setSelectedStatusesByType((prev) => ({
      ...prev,
      [exportType]: finalStatuses,
    }))
  }

  const handleExport = async () => {
    if (selectedExportTypes.size === 0) {
      addToast('Выберите хотя бы один тип экспорта', 'error')
      return
    }

    // Проверяем дату для типов, требующих фильтров
    const needsDate = Array.from(selectedExportTypes).some((type) => {
      const typeConfig = availableExportTypes.find((t) => t.id === type)
      return typeConfig?.hasFilters
    })

    if (needsDate && !dateRange?.from) {
      addToast('Выберите период экспорта', 'error')
      return
    }

    try {
      // Формируем параметры для экспорта
      const exportParams = needsDate
        ? {
            startDate: dateRange?.from ? dateRange.from.toISOString() : undefined,
            endDate: dateRange?.to ? dateRange.to.toISOString() : undefined,
          }
        : {}

      // Скачиваем файлы для каждого выбранного типа
      let successCount = 0
      let errorCount = 0

      for (const exportType of selectedExportTypes) {
        try {
          const typeConfig = availableExportTypes.find((t) => t.id === exportType)
          const statuses = selectedStatusesByType[exportType]

          // Если выбран псевдо-статус "Все статусы" (-1), отправляем undefined (чтобы получить все)
          // Иначе отправляем выбранные статусы, если они есть
          const apiStatuses = statuses.includes(ALL_STATUSES_VALUE)
            ? undefined
            : statuses.length > 0
              ? statuses
              : undefined

          const params = typeConfig?.hasFilters
            ? {
                ...exportParams,
                statuses: apiStatuses,
              }
            : {}

          const response = await exportData({ type: exportType, params })

          if (response?.data) {
            const filename = generateExportFilename(exportType)

            let csvBlob: Blob

            if (typeof response.data === 'string') {
              const normalized = normalizeCsvByExportType(response.data, exportType)
              csvBlob = new Blob([normalized], { type: 'text/csv;charset=utf-8;' })
            } else {
              // response.data — Blob: читаем текст, нормализуем, снова упаковываем
              const text = await response.data.text()
              const normalized = normalizeCsvByExportType(text, exportType)
              csvBlob = new Blob([normalized], { type: 'text/csv;charset=utf-8;' })
            }

            downloadFile(csvBlob, filename)
            successCount++
            // Задержка между скачиваниями, чтобы браузер корректно обработал несколько файлов
            await new Promise((resolve) => setTimeout(resolve, 300))
          }
        } catch (error) {
          console.error(`Export error for ${exportType}:`, error)
          errorCount++
        }
      }

      // Показываем результат (окно НЕ закрывается)
      if (successCount > 0 && errorCount === 0) {
        addToast(
          successCount === 1 ? 'Экспорт успешно завершен' : `Экспортировано ${successCount} файлов`,
          'success'
        )
      } else if (errorCount > 0) {
        addToast(
          successCount > 0
            ? `Загружено ${successCount}, ошибок: ${errorCount}`
            : `Ошибка при экспорте ${errorCount} файлов`,
          'error'
        )
      }
    } catch (error) {
      console.error('Export error:', error)
      addToast('Ошибка при экспорте данных', 'error')
    }
  }

  const handleClose = () => {
    if (!isExporting) {
      setModalExport(false)
      // Сбрасываем состояние при закрытии
      setSelectedExportTypes(new Set())
      setSelectedStatusesByType({
        balances: [],
        withdrawals: [],
        topups: [],
        cryptowithdrawals: [],
      })
      setDateRange(undefined)
    }
  }

  // Проверяем, требуется ли дата для хотя бы одного выбранного типа
  const needsDate = Array.from(selectedExportTypes).some((type) => {
    const typeConfig = availableExportTypes.find((t) => t.id === type)
    return typeConfig?.hasFilters
  })

  return (
    <BaseDialog
      isVisible={modalExport}
      onClose={handleClose}
      onAction={handleExport}
      actionButtonName={isExporting ? 'Экспорт...' : 'Создать экспорт'}
      title="Настройки экспорта"
      isLoading={isExporting}
    >
      <div className="flex flex-col gap-6">
        {/* Выбор даты */}
        <div className={`flex flex-col gap-2 ${needsDate ? '' : 'opacity-50 pointer-events-none'}`}>
          <p className="text-sm font-medium">Выберите дату</p>
          <DatePickerWithRange
            onDateChange={setDateRange}
            defaultValue={dateRange}
            variant="outline"
          />
        </div>

        {/* Выбор формата */}
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium">Выберите формат</p>
          <BaseButtonGroup
            options={EXPORT_FORMATS}
            value={['csv']}
            onChange={() => {}}
            multiple={false}
            className="flex-wrap"
            padding="sm"
          />
        </div>

        {/* Список типов экспорта */}
        <div className="flex flex-col gap-4">
          {availableExportTypes.map((type) => {
            const isSelected = selectedExportTypes.has(type.id)
            const statuses = STATUS_OPTIONS_BY_TYPE[type.id]
            const hasStatuses = statuses.length > 0
            const typeStatuses = selectedStatusesByType[type.id]

            return (
              <div key={type.id} className="flex flex-col gap-3">
                <BaseCheckbox
                  checked={isSelected}
                  onChange={() => handleExportTypeChange(type.id)}
                  fieldClassName="flex-row-reverse justify-between"
                  label={type.label}
                />

                {isSelected && hasStatuses && (
                  <BaseMultiSelect
                    options={[
                      ALL_STATUSES_OPTION,
                      ...statuses.map((status) => ({
                        value: status.value.toString(),
                        label: status.label,
                      })),
                    ]}
                    value={typeStatuses.map((s) => s.toString())}
                    onChange={(values) =>
                      handleStatusChange(
                        type.id,
                        values.map((v) => parseInt(v))
                      )
                    }
                    placeholder="Выберите статусы..."
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>
    </BaseDialog>
  )
}
